/**
 * saveSettingsConcurrently
 *
 * The BFF's UpdateSettings endpoint processes settings synchronously through
 * ECP, averaging ~5s per setting (observed range: 1–7s). When a batch of N
 * settings is sent in a single POST, the user waits N × ~5s with no feedback
 * (e.g. 42 settings ≈ 3.5 min).
 *
 * This utility breaks the batch into individual POST calls and runs them through
 * a concurrency-limited pool (default 5). Each setting resolves independently,
 * so the UI can show real-time per-setting progress (pending → completed/failed)
 * while keeping server load bounded. Failures are isolated — one setting failing
 * does not block or cancel the others.
 *
 * With concurrency of 5, a 42-setting batch completes in ~44s wall time
 * instead of ~210s sequential.
 */
import type { DesiredSettingValue } from '@crossnokaye/typescript-sdk/models/components';

/** Default concurrency limit for parallel save requests. */
export const CONCURRENT_SAVE_LIMIT = 8;

const defaultGetItemId = <TItem>(item: TItem): string => (item as DesiredSettingValue).settingId;

interface SaveSettingsConcurrentlyOptions<TItem = DesiredSettingValue> {
  settings: TItem[];
  updateFn: (settings: TItem[]) => Promise<unknown>;
  concurrency?: number;
  signal?: AbortSignal;
  getItemId?: (item: TItem) => string;
}

interface SaveSettingsConcurrentlyResult {
  completed: string[];
  failed: string[];
  aborted: boolean;
}

export async function saveSettingsConcurrently<TItem = DesiredSettingValue>({
  settings,
  updateFn,
  concurrency = CONCURRENT_SAVE_LIMIT,
  signal,
  getItemId = defaultGetItemId,
}: SaveSettingsConcurrentlyOptions<TItem>): Promise<SaveSettingsConcurrentlyResult> {
  const completed: string[] = [];
  const failed: string[] = [];
  let aborted = false;

  if (settings.length === 0) {
    return { completed, failed, aborted };
  }

  // Shared index counter — each worker increments atomically (single-threaded JS)
  // to pull the next unprocessed setting from the array.
  let nextIndex = 0;

  const processNext = async (): Promise<void> => {
    while (nextIndex < settings.length) {
      if (signal?.aborted) {
        aborted = true;
        return;
      }

      const index = nextIndex++;
      const setting = settings[index];
      const itemId = getItemId(setting);

      try {
        // Wrap each item in an array because updateFn expects TItem[] — we send
        // one at a time for granular progress.
        await updateFn([setting]);
        completed.push(itemId);
      } catch {
        failed.push(itemId);
      }
    }
  };

  // Spawn N workers that share the work queue. Each worker runs processNext()
  // which loops until all settings are claimed. This caps in-flight requests
  // to `concurrency` without needing an external semaphore library.
  const workers = Array.from({ length: Math.min(Math.max(concurrency, 1), settings.length) }, () =>
    processNext()
  );

  await Promise.all(workers);

  return { completed, failed, aborted };
}
